import { Form, Modal, Row } from 'antd';
import { Store } from 'antd/lib/form/interface';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useProfily from 'hooks/useProfily';

import { dokJinaInfoGet, dokJinaInfoPut, dokJinaZalozeniApi } from 'services/dokumenty/prikazy';
import leiRejstrkApi from 'services/lei/leiRejstrik';

import { EPodTypDokumentu, ETypDokumentu } from 'enums/dokumentyEnums';

import getLanguageObject from 'utils/getLanguageObject';
import { validateICO } from 'utils/validations/icoValidation';
import { validateNID } from 'utils/validations/nidValidation';
import pscValidation from 'utils/validations/pscValidation';
import { validateRdc } from 'utils/validations/rodneCisloValidation';

import { IJinaOsobaData } from './NovyDokument';

interface IRozcestnikJinaOsoba {
    nextStep: () => void;
    setZadostID: (value: number) => void;
    zadostID?: number;
    setJinaOsobaData: React.Dispatch<React.SetStateAction<IJinaOsobaData | undefined>>;
    jinaOsobaData?: IJinaOsobaData;
}

const RozcestnikJinaOsoba: React.FC<IRozcestnikJinaOsoba> = ({
    nextStep,
    setZadostID,
    zadostID,
    setJinaOsobaData,
    jinaOsobaData
}) => {
    const [form] = Form.useForm();
    const { setLevel } = useProfily();
    const { profily, dokumenty, cisStaty, language } = useContext(Context);
    const [isFirstStep, setIsFirstStep] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [modalErr, setModalErr] = useState<string>('');
    const [ico, setIco] = useState<string>();

    useEffect(() => {
        if (zadostID) {
            setLoading(true);
            dokJinaInfoGet(zadostID)
                .then(resp => {
                    setJinaOsobaData(pv => {
                        return {
                            ...pv!,
                            osobaData: resp
                        };
                    });
                    form.setFieldsValue(resp.spolecnost);
                })
                .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (ico) {
            setLoading(true);
            leiRejstrkApi({
                ic: ico,
                stat: 'CZ',
                lang: language,
                regauthor: 'RA000163'
            })
                .then(resp => {
                    form.setFieldsValue({
                        nazev: resp.legalname,
                        adma: resp.adresy[0]?.firstaddressline,
                        mema: resp.adresy[0]?.city,
                        pscm: resp.adresy[0]?.postalcode,
                        kost: resp.adresy[0]?.country
                    });
                })
                .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ico]);

    const onFinish = (value: Store) => {
        if (isFirstStep) {
            setJinaOsobaData(pv => {
                return {
                    ...pv!,
                    osobaData: {
                        spolecnost: {
                            idmb: value.idmb,
                            nazev: value.nazev,
                            // dicdph: value.dicdph,
                            // lei: value.lei,
                            adma: value.adma,
                            mema: value.mema,
                            pscm: value.pscm,
                            kost: value.kost,
                            nace: value.nace
                            /* predcisli: value.predcisli,
                    cislouctu: value.cislouctu,
                    banka: value.banka,
                    iban: value.iban */
                        },
                        osobazast: {
                            jmeno: value.jmeno,
                            prijmeni: value.prijmeni,
                            email: value.email,
                            telefon: value.telefon
                        }
                    }
                };
            });
            setIsFirstStep(false);
        } else if (zadostID && jinaOsobaData) {
            setLoading(true);
            dokJinaInfoPut(zadostID, jinaOsobaData.osobaData)
                .then(() => nextStep())
                .finally(() => setLoading(false));
        } else {
            setLoading(true);
            dokJinaZalozeniApi({
                idmb: jinaOsobaData?.osobaData.spolecnost.idmb!,
                nazev: jinaOsobaData?.osobaData.spolecnost.nazev!,
                stat: jinaOsobaData?.osobaData.spolecnost.kost!,
                typ: ETypDokumentu.PRIKAZY,
                podtyp: value.podtyp
            })
                .then(resp => {
                    if (resp.stav !== 'OK') {
                        setLoading(false);
                        setModalErr(resp.stav);
                    } else {
                        setZadostID(resp.id);
                        setLevel('dokumenty', {
                            ...dokumenty,
                            selectedZadost: {
                                iddok: resp.id,
                                typ: ETypDokumentu.PRIKAZY,
                                podtyp: value.podtyp
                            }
                        });
                        dokJinaInfoPut(resp.id, jinaOsobaData!.osobaData!)
                            .then(() => nextStep())
                            .finally(() => setLoading(false));
                    }
                })
                .catch(() => setLoading(false));
        }
    };

    return (
        <LoadingWrapper loading={loading}>
            <FormWrapper outForm={form} onFinish={onFinish}>
                {isFirstStep ? (
                    <>
                        <Title level={5} text={'DOK_JINA_IDENTIFIKACE_OSOBA'} />
                        <InputComponent
                            name="idmb"
                            label="JINA_OSOBA_IDMB_LABEL"
                            defaultValue={jinaOsobaData?.osobaData.spolecnost?.idmb}
                            maxLength={10}
                            rules={[
                                () => ({
                                    validator(_, value: string) {
                                        if (!value) return Promise.resolve();
                                        if (
                                            validateICO(value) === 0 ||
                                            validateRdc(value) === 0 ||
                                            validateNID(value) === 0
                                        )
                                            return Promise.resolve();
                                        return Promise.reject(<TranslateText>UPOZORNENI_NEVALIDNIRC</TranslateText>);
                                    }
                                })
                            ]}
                            onBlur={ev =>
                                validateICO(ev.target.value) === 0 &&
                                jinaOsobaData?.osobaData.spolecnost?.idmb !== ev.target.value &&
                                setIco(ev.target.value)
                            }
                        />
                        <InputComponent
                            initialValue={jinaOsobaData?.osobaData.spolecnost.nazev}
                            label="PRIKAZ_LIKVIDACE_NAZEV"
                            name="nazev"
                        />
                        <InputComponent
                            label="ULICE_CP"
                            name="adma"
                            initialValue={jinaOsobaData?.osobaData.spolecnost?.adma}
                            type="text"
                            maxLength={50}
                        />
                        <InputComponent
                            label="MESTO"
                            name="mema"
                            initialValue={jinaOsobaData?.osobaData.spolecnost?.mema}
                            type="text"
                            maxLength={50}
                        />
                        <InputComponent
                            label="PSC"
                            name="pscm"
                            initialValue={jinaOsobaData?.osobaData.spolecnost?.pscm}
                            maxLength={10}
                            type="text"
                            usePscFormater={true}
                            rules={pscValidation('kost')}
                        />
                        <SelectComponent
                            name="kost"
                            labelParagraph="STAT_SIDLA"
                            isFormItem={true}
                            defaultValue={jinaOsobaData?.osobaData.spolecnost?.kost}
                            data={cisStaty}
                        />
                        <Title level={5} text={'DOK_JINA_IDENTIFIKACE_OSOBA_ZAS'} />
                        <InputComponent
                            label="JMENO"
                            name="jmeno"
                            initialValue={profily?.jmeno}
                            disabled={true}
                            notRequired={true}
                        />
                        <InputComponent
                            label="PRIJMENI"
                            name="prijmeni"
                            initialValue={profily?.prijmeni}
                            disabled={true}
                            notRequired={true}
                        />
                        <InputComponent
                            label="EMAIL"
                            name="email"
                            initialValue={profily?.email}
                            disabled={true}
                            notRequired={true}
                        />
                        <InputComponent
                            label="TELEFON"
                            name="telefon"
                            type="tel"
                            initialValue={profily?.telefon1}
                            disabled={true}
                            notRequired={true}
                        />
                    </>
                ) : (
                    <SelectComponent
                        isFormItem={true}
                        data={[
                            {
                                value: EPodTypDokumentu.EMISELIKV,
                                labelcz: 'DOKUMENTY_PRIKAZ_' + EPodTypDokumentu.EMISELIKV
                            },
                            {
                                value: EPodTypDokumentu.EMISEFUZE,
                                labelcz: 'DOKUMENTY_PRIKAZ_' + EPodTypDokumentu.EMISEFUZE
                            },
                            {
                                value: EPodTypDokumentu.ZMENFUZE,
                                labelcz: 'DOKUMENTY_PRIKAZ_' + EPodTypDokumentu.ZMENFUZE
                            },
                            {
                                value: EPodTypDokumentu.ZMENJINA,
                                labelcz: 'DOKUMENTY_PRIKAZ_' + EPodTypDokumentu.ZMENJINA
                            },
                            {
                                value: EPodTypDokumentu.EMISELIST,
                                labelcz: 'DOKUMENTY_PRIKAZ_' + EPodTypDokumentu.EMISELIST
                            },
                            {
                                value: EPodTypDokumentu.VYPIS,
                                labelcz: 'DOKUMENTY_PRIKAZ_' + EPodTypDokumentu.VYPIS
                            }
                        ].sort((a, b) => {
                            const aLabel = getLanguageObject(language)[a.labelcz];
                            const bLabel = getLanguageObject(language)[b.labelcz];
                            if (aLabel < bLabel) return -1;
                            if (aLabel > bLabel) return 1;
                            return 0;
                        })}
                        name="podtyp"
                        labelParagraph={'TYP_DOKUMENTU'}
                    />
                )}
                <Row className="button-row">
                    {!isFirstStep && (
                        <ButtonComponent
                            buttontext="ZPET"
                            onClick={() => setIsFirstStep(true)}
                            id="OsobyZpetBtn"
                            type="default"
                        />
                    )}

                    <ButtonComponent buttontext="POKRACOVAT" onClick={() => form.submit()} id="isinPokracovatBtn" />
                </Row>
            </FormWrapper>
            <Modal
                title={<TranslateText>{'NOVY_DOKUMNET'}</TranslateText>}
                open={!!modalErr}
                onCancel={() => setModalErr('')}
                afterClose={() => {
                    setModalErr('');
                }}
                footer={
                    <Row>
                        <ButtonComponent
                            buttontext="ZAVRIT"
                            onClick={() => {
                                setModalErr('');
                            }}
                            id="emitentPrehledZavritBtn"
                        />
                    </Row>
                }
            >
                <Row>
                    <Paragraph text={'DOKUMENT_NOVY_ERR_' + modalErr} />
                </Row>
            </Modal>
        </LoadingWrapper>
    );
};

export default RozcestnikJinaOsoba;
